/**
 * Tarification de la livraison — logique PURE (aucun accès réseau, aucun React).
 *
 * ⚠️ Pourquoi ce module existe à part. Le barème `delivery_pricing` décide de
 * ce que paie chaque client et de ce que touche chaque livreur, pour TOUTES les
 * commandes suivantes. Depuis la migration 0054 le prix est PROGRESSIF : chaque
 * palier donne le prix À SA DISTANCE MAXI, un point à 0 km porte le prix minimum,
 * et le serveur interpole entre les deux (`_shared/pricing.ts`, le MÊME module
 * que l'aperçu ci-dessous). Une grille dont un prix BAISSE quand la distance
 * augmente ferait payer moins cher un client plus loin : elle est refusée ici,
 * AVANT l'enregistrement, et testée.
 */
import {
  type DeliveryTier,
  distanceMaxGrille,
  fraisServiceProgressif,
  type MinimaLivraison,
  type PrixLivraison,
  prixProgressif,
} from '../../../../supabase/functions/_shared/pricing.ts'

export type { MinimaLivraison } from '../../../../supabase/functions/_shared/pricing.ts'

export type ConditionMeteo = 'normal' | 'rain' | 'extreme_heat'

export const CONDITIONS_METEO: readonly ConditionMeteo[] = ['normal', 'rain', 'extreme_heat']

const LIBELLES_METEO: Record<ConditionMeteo, string> = {
  normal: 'Normale',
  rain: 'Pluie',
  extreme_heat: 'Forte chaleur',
}

/** Libellé français d'une condition météo. Une valeur inconnue est rendue telle quelle. */
export function libelleMeteo(condition: string): string {
  return LIBELLES_METEO[condition as ConditionMeteo] ?? condition
}

export function estConditionMeteo(v: unknown): v is ConditionMeteo {
  return typeof v === 'string' && (CONDITIONS_METEO as readonly string[]).includes(v)
}

/**
 * Un palier tel que saisi. Un nombre absent ou illisible est `NaN` : la
 * validation le signale, au lieu que l'écran n'enregistre « 0 » à la place.
 */
export interface PalierASaisir {
  minKm: number
  maxKm: number
  /** Ce que paie le client. */
  fraisClient: number
  /** Ce que touche le livreur. */
  partLivreur: number
  /**
   * Colonne `premium_fee` de la grille : INUTILISÉE depuis 0055 (Premium = prix
   * client × (1 − réduction)). Conservée telle quelle à l'enregistrement, ni
   * montrée ni contrôlée.
   */
  fraisPremium: number
}

export interface Probleme {
  /** Index (0-based, dans l'ordre fourni) du palier concerné ; null = tableau entier. */
  ligne: number | null
  message: string
}

export interface ResultatValidation {
  /** Bloquants : l'enregistrement est refusé tant qu'il en reste. */
  erreurs: Probleme[]
  /** Non bloquants : choix discutables mais possibles. */
  avertissements: Probleme[]
}

/** Plus grande distance que la colonne `numeric(4,1)` sait porter. */
export const KM_MAX = 999.9

/** Lit une saisie : virgule ou point décimal, espaces ignorés. Vide ou illisible → NaN. */
export function lireNombre(saisie: string): number {
  const net = String(saisie ?? '').trim().replace(/\s/g, '').replace(',', '.')
  if (net === '') return NaN
  return Number(net)
}

/** Vrai si `n` n'a pas plus d'une décimale (tolérance flottante). */
function uneDecimale(n: number): boolean {
  return Math.abs(n * 10 - Math.round(n * 10)) < 1e-9
}

/** Affichage d'une distance : « 3 », « 4,5 » (virgule française, jamais « 4.50000001 »). */
export function fmtKm(n: number): string {
  if (!Number.isFinite(n)) return '?'
  return String(Math.round(n * 10) / 10).replace('.', ',')
}

function fmtMontant(n: number): string {
  return `${Math.round(n).toLocaleString('fr-FR').replace(/ | /g, ' ')} FCFA`
}

/** Les mêmes paliers, classés du plus proche au plus éloigné (copie, ne modifie pas l'entrée). */
export function trierPaliers<T extends { minKm: number; maxKm: number }>(paliers: T[]): T[] {
  return [...paliers].sort((a, b) => {
    const ma = Number.isFinite(a.minKm) ? a.minKm : Infinity
    const mb = Number.isFinite(b.minKm) ? b.minKm : Infinity
    return ma - mb || (Number.isFinite(a.maxKm) ? a.maxKm : Infinity) - (Number.isFinite(b.maxKm) ? b.maxKm : Infinity)
  })
}

/**
 * Valide un barème complet. Les paliers sont fournis DANS L'ORDRE OÙ L'ÉCRAN
 * LES MONTRE : c'est cet ordre qui est jugé (« classés »), pas un ordre
 * recalculé, sinon l'opérateur ne saurait pas que son tableau est dans le
 * désordre.
 */
export function validerPaliers(paliers: PalierASaisir[], minima?: MinimaASaisir): ResultatValidation {
  const erreurs: Probleme[] = []
  const avertissements: Probleme[] = []
  const nom = (i: number) => `Palier ${i + 1}`

  if (paliers.length === 0) {
    erreurs.push({
      ligne: null,
      message:
        'Il faut au moins un palier. Sans barème, la livraison serait facturée 0 FCFA au client et au livreur.',
    })
    return { erreurs, avertissements }
  }

  // 1. Chaque palier pris isolément ───────────────────────────────────────
  const distancesOk: boolean[] = []
  paliers.forEach((p, i) => {
    let ok = true
    for (const [champ, v] of [
      ['distance mini', p.minKm],
      ['distance maxi', p.maxKm],
    ] as const) {
      if (!Number.isFinite(v)) {
        erreurs.push({ ligne: i, message: `${nom(i)} : ${champ} manquante ou illisible.` })
        ok = false
      } else if (v < 0 || v > KM_MAX) {
        erreurs.push({ ligne: i, message: `${nom(i)} : ${champ} doit être comprise entre 0 et ${fmtKm(KM_MAX)} km.` })
        ok = false
      } else if (!uneDecimale(v)) {
        erreurs.push({ ligne: i, message: `${nom(i)} : ${champ} n'accepte qu'une décimale (par exemple 4,5 km).` })
        ok = false
      }
    }
    if (ok && p.minKm >= p.maxKm) {
      erreurs.push({
        ligne: i,
        message: `${nom(i)} : la distance mini (${fmtKm(p.minKm)} km) doit être inférieure à la distance maxi (${fmtKm(p.maxKm)} km).`,
      })
      ok = false
    }
    distancesOk.push(ok)

    for (const [champ, v] of [
      ['Tarif client', p.fraisClient],
      ['Part livreur', p.partLivreur],
    ] as const) {
      if (!Number.isFinite(v)) erreurs.push({ ligne: i, message: `${nom(i)} : ${champ} manquant ou illisible.` })
      else if (!Number.isInteger(v)) erreurs.push({ ligne: i, message: `${nom(i)} : ${champ} doit être un montant entier en FCFA.` })
      else if (v < 0) erreurs.push({ ligne: i, message: `${nom(i)} : ${champ} ne peut pas être négatif.` })
    }
  })

  // 2. Ordre d'affichage ─────────────────────────────────────────────────
  for (let i = 1; i < paliers.length; i++) {
    const a = paliers[i - 1]!
    const b = paliers[i]!
    if (Number.isFinite(a.minKm) && Number.isFinite(b.minKm) && b.minKm < a.minKm) {
      erreurs.push({
        ligne: i,
        message: `${nom(i)} (à partir de ${fmtKm(b.minKm)} km) est placé après un palier qui commence plus loin (${fmtKm(a.minKm)} km) : classez les paliers du plus proche au plus éloigné.`,
      })
    }
  }

  // 3. Continuité, sur les paliers classés (indépendante de l'ordre d'affichage) ──
  const classes = paliers
    .map((p, i) => ({ p, i }))
    .filter(({ i }) => distancesOk[i])
    .sort((x, y) => x.p.minKm - y.p.minKm || x.p.maxKm - y.p.maxKm)

  if (classes.length > 0) {
    const premier = classes[0]!
    if (premier.p.minKm !== 0) {
      erreurs.push({
        ligne: premier.i,
        message: `Le premier palier doit commencer à 0 km (il commence à ${fmtKm(premier.p.minKm)} km) : les livraisons plus proches ne seraient rattachées à aucun palier.`,
      })
    }
  }
  for (let k = 1; k < classes.length; k++) {
    const prec = classes[k - 1]!
    const cour = classes[k]!
    if (cour.p.minKm > prec.p.maxKm) {
      erreurs.push({
        ligne: cour.i,
        message: `Trou entre ${fmtKm(prec.p.maxKm)} km et ${fmtKm(cour.p.minKm)} km : ces distances ne correspondent à aucun palier. Le maxi d'un palier doit être égal au mini du suivant.`,
      })
    } else if (cour.p.minKm < prec.p.maxKm) {
      erreurs.push({
        ligne: cour.i,
        message: `${nom(cour.i)} chevauche ${nom(prec.i)} entre ${fmtKm(cour.p.minKm)} km et ${fmtKm(prec.p.maxKm)} km : le maxi d'un palier doit être égal au mini du suivant.`,
      })
    }
  }

  // 4. Un prix ne baisse jamais quand la distance augmente (0054) ─────────
  //    Jugé sur les paliers classés par distance maxi — le point de la courbe.
  //    Le minimum à 0 km n'entre pas dans ce contrôle : un palier sous le
  //    minimum est permis (le minimum s'applique), il est seulement signalé.
  const parMaxi = classes.slice().sort((x, y) => x.p.maxKm - y.p.maxKm || x.p.minKm - y.p.minKm)
  for (const [champ, libelle] of COLONNES) {
    for (let k = 1; k < parMaxi.length; k++) {
      const a = parMaxi[k - 1]!
      const b = parMaxi[k]!
      const va = a.p[champ]
      const vb = b.p[champ]
      if (Number.isFinite(va) && Number.isFinite(vb) && vb < va) {
        erreurs.push({
          ligne: b.i,
          message: `${libelle} baisse quand la distance augmente : ${fmtMontant(va)} à ${fmtKm(a.p.maxKm)} km, puis ${fmtMontant(vb)} à ${fmtKm(b.p.maxKm)} km. Un client plus loin paierait moins cher : remontez le prix à ${fmtKm(b.p.maxKm)} km (au moins ${fmtMontant(va)}).`,
        })
      }
    }
  }

  // 5. Avertissements (choix possible, mais à vérifier) ───────────────────
  if (minima) {
    for (const [champ, libelle, cle] of COLONNES) {
      const sous = paliers
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => Number.isFinite(p[champ]) && p[champ] < minima[cle])
      for (const { p, i } of sous) {
        avertissements.push({
          ligne: i,
          message: `${nom(i)} : ${libelle.toLowerCase()} à ${fmtKm(p.maxKm)} km (${fmtMontant(p[champ])}) est sous le prix minimum (${fmtMontant(minima[cle])}) — c'est le minimum qui s'applique à cette distance.`,
        })
      }
    }
  }
  paliers.forEach((p, i) => {
    if (Number.isFinite(p.fraisClient) && Number.isFinite(p.partLivreur) && p.partLivreur > p.fraisClient) {
      avertissements.push({
        ligne: i,
        message: `${nom(i)} : le livreur touche ${fmtMontant(p.partLivreur)} alors que le client paie ${fmtMontant(p.fraisClient)} — chaque livraison est à perte de ${fmtMontant(p.partLivreur - p.fraisClient)}.`,
      })
    }
  })

  return { erreurs, avertissements }
}

/** Les trois colonnes de prix : champ saisi, libellé, clé des minima. */
const COLONNES: ReadonlyArray<readonly ['fraisClient' | 'partLivreur', string, 'client' | 'driver']> = [
  ['fraisClient', 'Le prix client', 'client'],
  ['partLivreur', 'La paie livreur', 'driver'],
]

// ── Prix minimum (point à 0 km) ──────────────────────────────────────────

/** Prix à 0 km. Le minimum Premium n'est plus saisi (0055) : Premium = client × (1 − réduction). */
export interface MinimaASaisir {
  client: number
  driver: number
}

/** Les trois minima : entiers positifs. Erreurs bloquantes. */
export function validerMinima(m: MinimaASaisir): Probleme[] {
  const erreurs: Probleme[] = []
  for (const [cle, libelle] of [
    ['client', 'Prix minimum client'],
    ['driver', 'Paie minimum livreur'],
  ] as const) {
    const v = m[cle]
    if (!Number.isFinite(v)) erreurs.push({ ligne: null, message: `${libelle} manquant ou illisible.` })
    else if (!Number.isInteger(v) || v < 0) erreurs.push({ ligne: null, message: `${libelle} : montant entier positif en FCFA attendu.` })
  }
  return erreurs
}

// ── Frais de service selon la distance (0054) ────────────────────────────

export interface FraisServiceASaisir {
  /** Frais à 0 km (`service_fee`). */
  bas: number
  /** Frais à la distance maxi de la grille (`service_fee_haut`) ; null = frais fixes. */
  haut: number | null
}

/** Entiers positifs, et des frais qui ne baissent pas avec la distance. Erreurs bloquantes. */
export function validerFraisService(f: FraisServiceASaisir): Probleme[] {
  const erreurs: Probleme[] = []
  const lire = (v: number, libelle: string) => {
    if (!Number.isFinite(v)) erreurs.push({ ligne: null, message: `${libelle} manquants ou illisibles.` })
    else if (!Number.isInteger(v) || v < 0) erreurs.push({ ligne: null, message: `${libelle} : montant entier positif en FCFA attendu.` })
  }
  lire(f.bas, 'Frais de service à 0 km')
  if (f.haut !== null) lire(f.haut, 'Frais de service à la distance maxi')
  if (f.haut !== null && Number.isFinite(f.bas) && Number.isFinite(f.haut) && f.haut < f.bas)
    erreurs.push({
      ligne: null,
      message: `Les frais de service baisseraient avec la distance (${fmtMontant(f.bas)} à 0 km, ${fmtMontant(f.haut)} au maximum) : la limite haute doit être au moins égale à la limite basse.`,
    })
  return erreurs
}

// ── Aperçu (avant enregistrement) ────────────────────────────────────────

/** Distances montrées dans l'aperçu de l'écran Tarifs (décision du 12/10/2026). */
export const DISTANCES_APERCU: readonly number[] = [0.1, 0.5, 1, 2, 3, 5, 8, 12, 18]

export interface LigneApercu extends PrixLivraison {
  km: number
  /** Frais de service à cette distance. */
  service: number
}

/**
 * Prix client / Premium / paie livreur aux distances de l'aperçu, avec le MÊME
 * moteur que le serveur. Les paliers illisibles sont ignorés (l'écran les
 * signale déjà en erreur) : l'aperçu reste lisible pendant la saisie.
 */
export function apercuPrix(
  paliers: PalierASaisir[],
  minima: MinimaASaisir,
  fraisService: FraisServiceASaisir = { bas: 0, haut: null },
  /** Réduction Premium en % (0055). */
  reductionPremiumPct: number = 50,
  distances: readonly number[] = DISTANCES_APERCU,
): LigneApercu[] {
  const lisibles: DeliveryTier[] = paliers
    .filter((p) => [p.minKm, p.maxKm, p.fraisClient, p.partLivreur].every(Number.isFinite))
    .map((p) => ({
      radius_min_km: p.minKm,
      radius_max_km: p.maxKm,
      client_fee: p.fraisClient,
      driver_pay: p.partLivreur,
      premium_fee: 0, // inutilisé (0055)
    }))
  const m: MinimaLivraison = {
    client: Number.isFinite(minima.client) ? minima.client : 0,
    premium: 0, // inutilisé (0055)
    driver: Number.isFinite(minima.driver) ? minima.driver : 0,
  }
  const bas = Number.isFinite(fraisService.bas) ? fraisService.bas : 0
  const haut = fraisService.haut !== null && Number.isFinite(fraisService.haut) ? fraisService.haut : null
  const dMax = distanceMaxGrille(lisibles)
  return distances.map((km) => ({
    km,
    ...prixProgressif(lisibles, m, km, Number.isFinite(reductionPremiumPct) ? reductionPremiumPct : 0),
    service: fraisServiceProgressif(bas, haut, dMax, km),
  }))
}

// ── Taux de commission saisi au dispatch (0055) ──────────────────────────

/**
 * Lit un taux saisi en POURCENTAGE (« 10 », « 12,5 ») et rend la FRACTION à
 * écrire (0,10). Les colonnes sont en `numeric(4,2)` : seuls les pourcentages
 * ENTIERS sont stockables (12,5 % deviendrait 13 % en silence) — on le refuse.
 * Plafond 50 %, comme la contrainte de la base.
 */
export function lireTauxCommission(saisie: string): { ok: true; taux: number } | { ok: false; message: string } {
  const pct = lireNombre(saisie)
  if (!Number.isFinite(pct)) return { ok: false, message: 'Taux attendu, en % (par exemple 10).' }
  if (pct < 0 || pct > 50) return { ok: false, message: 'Taux attendu entre 0 et 50 %.' }
  if (!Number.isInteger(pct)) return { ok: false, message: 'Taux en pourcentage entier (la base ne garde pas les décimales).' }
  return { ok: true, taux: pct / 100 }
}

/** Minimum de commande propre au restaurant : vide = minimum plateforme (null), sinon entier positif. */
export function lireMinimumCommande(saisie: string): { ok: true; valeur: number | null } | { ok: false; message: string } {
  if (String(saisie ?? '').trim() === '') return { ok: true, valeur: null }
  const n = lireNombre(saisie)
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return { ok: false, message: 'Montant entier positif en FCFA, ou vide.' }
  return { ok: true, valeur: n }
}
